import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuPaintedButton from '../MenuPaintedButton';
import {
  RESIGN_DIALOG_BACKGROUND_PATH,
  RESIGN_DIALOG_CANCEL_BUTTON_PATH,
  RESIGN_DIALOG_CONFIRM_BUTTON_PATH,
} from '../../config/welcomeAssets';
import { resolvePublicAssetBackgroundUrl } from '../../utils/resolvePublicAssetUrl';
import './ResignConfirmDialog.css';

/**
 * Resign confirmation modal shared by BattleUI and BattleOverview.
 */
export function ResignConfirmDialog({
  isOpen,
  isMobile,
  onConfirm,
  onCancel,
  dialogFocus: controlledFocus,
  onDialogFocusChange,
  confirmButtonRef,
  cancelButtonRef,
}) {
  const { t } = useTranslation();
  const [internalFocus, setInternalFocus] = useState('cancel');
  const dialogFocus = controlledFocus !== undefined ? controlledFocus : internalFocus;
  const setDialogFocus = onDialogFocusChange ?? setInternalFocus;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setDialogFocus('cancel');
    const focusTarget = cancelButtonRef?.current;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  }, [isOpen, cancelButtonRef, setDialogFocus]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="presentation"
      data-resign-dialog-overlay
      className="resign-confirm-dialog-overlay"
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="resign-dialog-title"
        className={[
          'resign-confirm-dialog-panel',
          isMobile ? 'resign-confirm-dialog-panel--mobile' : '',
        ].filter(Boolean).join(' ')}
        style={{ '--resign-dialog-background-image': resolvePublicAssetBackgroundUrl(RESIGN_DIALOG_BACKGROUND_PATH) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="resign-confirm-dialog-inner">
          <h3 id="resign-dialog-title" className="resign-confirm-dialog-title">
            {t('battleUI.resignConfirm')}
          </h3>
          <p className="resign-confirm-dialog-message">
            {t('battleUI.resignMessage')}
          </p>
          <div className="resign-confirm-dialog-actions">
            <MenuPaintedButton
              ref={confirmButtonRef}
              type="button"
              tabIndex={0}
              onClick={onConfirm}
              className={[
                'resign-confirm-dialog-button',
                'resign-confirm-dialog-button--confirm',
                dialogFocus === 'confirm' ? 'resign-confirm-dialog-button--focused' : '',
              ].filter(Boolean).join(' ')}
              imagePath={RESIGN_DIALOG_CONFIRM_BUTTON_PATH}
              onMouseEnter={() => setDialogFocus('confirm')}
              onFocus={() => setDialogFocus('confirm')}
            >
              {t('battleUI.yesResign')}
            </MenuPaintedButton>
            <MenuPaintedButton
              ref={cancelButtonRef}
              type="button"
              tabIndex={0}
              onClick={onCancel}
              className={[
                'resign-confirm-dialog-button',
                'resign-confirm-dialog-button--cancel',
                dialogFocus === 'cancel' ? 'resign-confirm-dialog-button--focused' : '',
              ].filter(Boolean).join(' ')}
              imagePath={RESIGN_DIALOG_CANCEL_BUTTON_PATH}
              onMouseEnter={() => setDialogFocus('cancel')}
              onFocus={() => setDialogFocus('cancel')}
            >
              {t('common.cancel')}
            </MenuPaintedButton>
          </div>
        </div>
      </div>
    </div>
  );
}
